import React, {memo} from 'react';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import styles from './HomeWhyChoose.module.scss';

// Stack geometry: the card stack is pure CSS `position: sticky`. Each card sticks a little
// lower than the one before it, so the parked cards' top edges stay visible as a stack.
const NAV_HEIGHT = 64; // .nav_grid, the sticky site navbar
const STACK_TOP = NAV_HEIGHT + 24; // where the first card parks
const PEEK = 18; // visible sliver of each parked card

// Three tints cycled per card (`index % 3`) — see `.bg0`..`.bg2` in the module.
const CARD_BACKGROUND_CLASSES = [styles.bg0, styles.bg1, styles.bg2] as const;

// MUI Check path (inlined: not in plain/Icon).
const CHECK_PATH = 'M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z';

interface WhyChooseRow {
    title: string;
    description: string;
    /** CDN URL or a statically imported image — `cdnImage` can return either. */
    image?: string | StaticImageData;
    imageAlt?: string;
    /**
     * The artwork's intrinsic size. When given, the image keeps its own proportions at full column width instead of
     * sitting inside the fixed-height box — wide photos then fill the column with no empty bands above and below.
     */
    imageWidth?: number;
    imageHeight?: number;
    /** Optional check-marked list under the description (laid out in columns on an image-less row). */
    points?: string[];
}

interface HomeWhyChooseProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    data: WhyChooseRow[];
    /** From lg, let the heading run the full container width (one line) instead of the standard 64rem. */
    wideHeading?: boolean;
}

const HomeWhyChoose = ({eyebrow, heading, subheading, data, wideHeading = false}: HomeWhyChooseProps) => (
    <Container>
        <div className={styles.root}>
            <div className={styles.header}>
                <SectionHeader
                    eyebrow={eyebrow}
                    heading={heading}
                    subheading={subheading}
                    className={wideHeading ? styles.wideHeading : undefined}
                    subheadingClassName={`generative-transforming-subheading${wideHeading ? ` ${styles.wideHeadingSub}` : ''}`}
                />
            </div>

            <div className={styles.stack}>
                {data.map((row, index) => {
                    const isLast = index === data.length - 1;
                    const cardClass = [
                        styles.card,
                        CARD_BACKGROUND_CLASSES[index % CARD_BACKGROUND_CLASSES.length],
                        isLast && styles.cardLast
                    ]
                        .filter(Boolean)
                        .join(' ');
                    // Per-card sticky offset/stacking order, consumed from `md` up by the module.
                    const cardStyle = {
                        '--stack-top': `${STACK_TOP + index * PEEK}px`,
                        '--stack-z': index + 1
                    } as React.CSSProperties;

                    return (
                        <div key={row.title} className={cardClass} style={cardStyle}>
                            <div className={styles.grid}>
                                <div className={`${styles.item} ${row.image ? styles.textCol : styles.fullCol}`}>
                                    <h3 className={`font-headers ${styles.title}`}>{row.title}</h3>
                                    <p className={`font-regular-text ${styles.desc}`}>{row.description ? parse(row.description) : ''}</p>
                                    {!!row.points?.length && (
                                        <ul className={`${styles.points} ${row.image ? '' : styles.pointsWide}`}>
                                            {row.points.map((point) => (
                                                <li key={point} className={`font-regular-text ${styles.point}`}>
                                                    <svg
                                                        className={styles.pointIcon}
                                                        viewBox="0 0 24 24"
                                                        focusable="false"
                                                        aria-hidden="true"
                                                    >
                                                        <path d={CHECK_PATH} />
                                                    </svg>
                                                    {point}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {row.image && (
                                    <div className={`${styles.item} ${styles.imageCol}`}>
                                        {row.imageWidth && row.imageHeight ? (
                                            <Image
                                                src={row.image}
                                                alt={row.imageAlt ?? row.title}
                                                width={row.imageWidth}
                                                height={row.imageHeight}
                                                sizes="(max-width: 900px) 100vw, 500px"
                                                className={styles.imageNatural}
                                            />
                                        ) : (
                                            <div className={styles.imageWrap}>
                                                <Image
                                                    src={row.image}
                                                    alt={row.imageAlt ?? row.title}
                                                    fill
                                                    sizes="(max-width: 900px) 70vw, 460px"
                                                    style={{objectFit: 'contain'}}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    </Container>
);

export default memo(HomeWhyChoose);
